import { useState, useEffect } from 'react';
import API from '../../utils/api';
import PageHeader from '../../components/common/PageHeader';
import Badge from '../../components/common/Badge';
import EmptyState from '../../components/common/EmptyState';
import {
  HiOutlineBell, HiOutlineTrash, HiOutlinePlus, HiOutlineX,
  HiOutlineCheck, HiOutlineSpeakerphone, HiOutlineUserAdd,
  HiOutlineLocationMarker, HiOutlineUserCircle,
} from 'react-icons/hi';

const PRIORITY_STYLES = {
  urgent: { variant: 'danger',  label: 'জরুরি',        icon: '🔴' },
  high:   { variant: 'warning', label: 'গুরুত্বপূর্ণ', icon: '🟠' },
  normal: { variant: 'neutral', label: 'সাধারণ',       icon: '🔵' },
  low:    { variant: 'neutral', label: 'নিম্ন',        icon: '⚪' },
};

const TYPE_ICONS = {
  work_status:     '📋',
  site_assignment: '📍',
  alert:           '🚨',
  general:         '📢',
};

const AdminAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '', message: '', type: 'general', priority: 'normal',
  });
  const [assignments, setAssignments] = useState([{ worker_id: '', site_id: '' }]);

  const fetchData = async () => {
    try {
      const [annRes, workRes, siteRes] = await Promise.all([
        API.get('/admin/announcements'),
        API.get('/admin/workers?role=worker&limit=100'),
        API.get('/admin/sites?limit=100'),
      ]);
      setAnnouncements(annRes.data.announcements);
      setWorkers(workRes.data.workers || []);
      setSites(siteRes.data.sites || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // Build message from assignments
  const buildAssignmentMessage = (assigns) => {
    return assigns
      .filter(a => a.worker_id && a.site_id)
      .map(a => {
        const w = workers.find(x => x.id === Number(a.worker_id));
        const s = sites.find(x => x.id === Number(a.site_id));
        return `🔹 ${w?.name || '?'} → ${s?.name || '?'}`;
      })
      .join('\n');
  };

  const resetForm = () => {
    setForm({ title: '', message: '', type: 'general', priority: 'normal' });
    setAssignments([{ worker_id: '', site_id: '' }]);
    setError('');
  };

  const handleQuickPost = (title, message, type, priority) => {
    resetForm();
    if (type === 'site_assignment') {
      // For site assignment, pre-fill title and leave message to be built
      setForm({ title: title || 'আজকের সাইট বরাদ্দ', message: '', type, priority });
    } else {
      setForm({ title, message, type, priority });
    }
    setShowForm(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;

    // For site_assignment, build message from assignments if message is empty
    let message = form.message;
    if (form.type === 'site_assignment' && !message.trim()) {
      message = buildAssignmentMessage(assignments);
    }
    if (!message.trim()) return;

    // Collect targeted worker IDs from assignments
    const targeted_worker_ids = form.type === 'site_assignment'
      ? assignments.map(a => Number(a.worker_id)).filter(id => id > 0)
      : [];

    setSaving(true);
    setError('');
    try {
      await API.post('/admin/announcements', { ...form, message, targeted_worker_ids });
      resetForm();
      setShowForm(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'ত্রুটি');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('নিশ্চিত? এই ঘোষণা মুছে ফেলা হবে।')) return;
    try { await API.delete(`/admin/announcements/${id}`); fetchData(); }
    catch (e) { console.error(e); }
  };

  const addAssignmentRow = () => {
    setAssignments([...assignments, { worker_id: '', site_id: '' }]);
  };

  const removeAssignmentRow = (idx) => {
    if (assignments.length <= 1) return;
    setAssignments(assignments.filter((_, i) => i !== idx));
  };

  const updateAssignment = (idx, field, value) => {
    const updated = assignments.map((a, i) => i === idx ? { ...a, [field]: value } : a);
    setAssignments(updated);
    // Auto-update message preview
    const built = buildAssignmentMessage(updated);
    if (built) setForm(f => ({ ...f, message: built }));
  };

  const isSiteAssignment = form.type === 'site_assignment';

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
    </div>
  );

  const fmtDate = (d) => {
    const dt = new Date(d);
    return dt.toLocaleDateString('bn-BD', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="ঘোষণা"
        subtitle="ওয়ার্কারদের জন্য নোটিশ ও এলার্ট পাঠান"
      />

      {/* Quick action buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <button onClick={() => handleQuickPost('আজকে কাজ বন্ধ', 'আজকের সকল কাজ বন্ধ থাকবে।', 'work_status', 'urgent')}
          className="card flex items-center gap-3 hover:shadow-pop hover:-translate-y-0.5 transition-all duration-200 text-left">
          <div className="w-11 h-11 rounded-xl bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center text-xl flex-shrink-0">🚫</div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink-900 dark:text-white">কাজ বন্ধ</p>
            <p className="text-xs text-ink-500 dark:text-ink-400">আজকে কাজ থাকবে না</p>
          </div>
        </button>
        <button onClick={() => handleQuickPost('আজকের সাইট বরাদ্দ', '', 'site_assignment', 'high')}
          className="card flex items-center gap-3 hover:shadow-pop hover:-translate-y-0.5 transition-all duration-200 text-left ring-2 ring-amber-300 dark:ring-amber-700">
          <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center text-xl flex-shrink-0">📍</div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink-900 dark:text-white">সাইট বরাদ্দ</p>
            <p className="text-xs text-ink-500 dark:text-ink-400">ওয়ার্কার + সাইট সিলেক্ট</p>
          </div>
        </button>
        <button onClick={() => handleQuickPost('', '', 'general', 'normal')}
          className="card flex items-center gap-3 hover:shadow-pop hover:-translate-y-0.5 transition-all duration-200 text-left">
          <div className="w-11 h-11 rounded-xl bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center text-xl flex-shrink-0">📢</div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink-900 dark:text-white">নতুন নোটিশ</p>
            <p className="text-xs text-ink-500 dark:text-ink-400">সাধারণ ঘোষণা দিন</p>
          </div>
        </button>
        <button onClick={() => handleQuickPost('', '', 'alert', 'urgent')}
          className="card flex items-center gap-3 hover:shadow-pop hover:-translate-y-0.5 transition-all duration-200 text-left">
          <div className="w-11 h-11 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center text-xl flex-shrink-0">🚨</div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink-900 dark:text-white">এলার্ট</p>
            <p className="text-xs text-ink-500 dark:text-ink-400">জরুরি নোটিফিকেশন</p>
          </div>
        </button>
      </div>

      {/* Create form modal */}
      {showForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/60 backdrop-blur-sm p-3"
          onClick={() => !saving && setShowForm(false)}>
          <div className="bg-white dark:bg-ink-800 rounded-2xl shadow-pop w-full max-w-xl p-5 sm:p-6 animate-fade-in max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-ink-900 dark:text-white flex items-center gap-2">
                <HiOutlineSpeakerphone className="w-6 h-6 text-brand-600" />
                {isSiteAssignment ? 'সাইট বরাদ্দ' : 'নতুন ঘোষণা'}
              </h3>
              <button onClick={() => { setShowForm(false); resetForm(); }}
                className="p-1.5 text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-700 rounded-lg transition-colors">
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-3 p-3 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-xl text-rose-700 dark:text-rose-200 text-sm">{error}</div>
            )}

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="input-label">শিরোনাম <span className="text-rose-500">*</span></label>
                <input type="text" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                  className="input-field" placeholder="যেমন: আজকের সাইট বরাদ্দ" required />
              </div>

              {/* Site assignment: worker + site selector */}
              {isSiteAssignment && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
                      <HiOutlineUserAdd className="w-4 h-4" />
                      ওয়ার্কার → সাইট বরাদ্দ দিন
                    </p>
                    <button type="button" onClick={addAssignmentRow}
                      className="text-xs font-semibold text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800 px-2.5 py-1 rounded-lg transition-colors flex items-center gap-1">
                      <HiOutlinePlus className="w-3.5 h-3.5" /> আরও যোগ
                    </button>
                  </div>

                  {assignments.map((a, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <HiOutlineUserCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                        <select value={a.worker_id} onChange={(e) => updateAssignment(idx, 'worker_id', e.target.value)}
                          className="input-field pl-9 text-sm">
                          <option value="">ওয়ার্কার নির্বাচন</option>
                          {workers.filter(w => w.is_active !== false).map(w => (
                            <option key={w.id} value={w.id}>{w.name} ({w.phone})</option>
                          ))}
                        </select>
                      </div>
                      <span className="text-amber-600 dark:text-amber-400 font-bold">→</span>
                      <div className="flex-1 relative">
                        <HiOutlineLocationMarker className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                        <select value={a.site_id} onChange={(e) => updateAssignment(idx, 'site_id', e.target.value)}
                          className="input-field pl-9 text-sm">
                          <option value="">সাইট নির্বাচন</option>
                          {sites.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                      <button type="button" onClick={() => removeAssignmentRow(idx)}
                        disabled={assignments.length <= 1}
                        className="p-2 text-ink-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors disabled:opacity-30">
                        <HiOutlineX className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {/* Preview built message */}
                  {assignments.some(a => a.worker_id && a.site_id) && (
                    <div className="mt-2 p-3 bg-white dark:bg-ink-800 border border-amber-200 dark:border-amber-700 rounded-lg">
                      <p className="text-[10px] text-amber-600 dark:text-amber-400 uppercase tracking-wider font-semibold mb-1">পূর্বরূপ</p>
                      <p className="text-sm text-ink-700 dark:text-ink-300 whitespace-pre-line">{buildAssignmentMessage(assignments)}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Message textarea — only show for non-site_assignment or if user wants to edit */}
              <div>
                <label className="input-label">
                  বার্তা
                  {isSiteAssignment && <span className="text-xs text-ink-400 font-normal ml-1">(ঐচ্ছিক — উপর থেকে auto তৈরি হবে)</span>}
                  {!isSiteAssignment && <span className="text-rose-500"> *</span>}
                </label>
                <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})}
                  className="input-field min-h-[80px] resize-y" placeholder={isSiteAssignment ? 'উপর থেকে সিলেক্ট করলে auto তৈরি হবে...' : 'আপনার বার্তা লিখুন...'}
                  required={!isSiteAssignment} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="input-label">ধরন</label>
                  <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="input-field">
                    <option value="general">📢 সাধারণ</option>
                    <option value="work_status">📋 কাজের অবস্থা</option>
                    <option value="site_assignment">📍 সাইট বরাদ্দ</option>
                    <option value="alert">🚨 এলার্ট</option>
                  </select>
                </div>
                <div>
                  <label className="input-label">প্রায়োরিটি</label>
                  <select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})} className="input-field">
                    <option value="normal">🔵 সাধারণ</option>
                    <option value="high">🟠 গুরুত্বপূর্ণ</option>
                    <option value="urgent">🔴 জরুরি</option>
                    <option value="low">⚪ নিম্ন</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-ink-100 dark:border-ink-700">
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="btn-secondary">বাতিল</button>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? 'পাঠানো হচ্ছে...' : <><HiOutlineCheck className="w-5 h-5" /> ঘোষণা দিন</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Announcements list */}
      <div className="card p-0 overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-ink-900 dark:text-white flex items-center gap-2">
            <span className="w-1.5 h-6 rounded-full bg-brand-500" />
            সকল ঘোষণা
            <span className="text-xs text-ink-400 ml-1">{announcements.length} টি</span>
          </h2>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="btn-primary btn-sm">
            <HiOutlinePlus className="w-4 h-4" /> নতুন
          </button>
        </div>

        {announcements.length === 0 ? (
          <EmptyState icon="🔔" title="কোনো ঘোষণা নেই" description="ওপরের বাটন ক্লিক করে প্রথম ঘোষণা তৈরি করুন।" />
        ) : (
          <div className="divide-y divide-ink-100 dark:divide-ink-700">
            {announcements.map((a) => {
              const ps = PRIORITY_STYLES[a.priority] || PRIORITY_STYLES.normal;
              const typeIcon = TYPE_ICONS[a.type] || '📢';
              return (
                <div key={a.id} className="p-5 hover:bg-ink-50 dark:hover:bg-ink-900/30 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0
                        ${a.priority === 'urgent' ? 'bg-rose-100 dark:bg-rose-900/40' :
                          a.priority === 'high' ? 'bg-amber-100 dark:bg-amber-900/40' :
                          'bg-brand-100 dark:bg-brand-900/40'}`}>
                        {typeIcon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold text-ink-900 dark:text-white text-sm">{a.title}</h3>
                          <Badge variant={ps.variant} dot><span className="flex items-center gap-1">{ps.icon} {ps.label}</span></Badge>
                          {a.is_active && <Badge variant="success" dot>সক্রিয়</Badge>}
                        </div>
                        <p className="text-sm text-ink-600 dark:text-ink-400 whitespace-pre-line">{a.message}</p>
                        <div className="flex items-center gap-3 mt-2 text-[11px] text-ink-500 dark:text-ink-400">
                          <span>👤 {a.creator?.name || '—'}</span>
                          <span>🕐 {fmtDate(a.created_at)}</span>
                          {a.type === 'site_assignment' && <Badge variant="warning" className="text-[10px]">📍 সাইট বরাদ্দ</Badge>}
                          {a.targeted_worker_ids && <Badge variant="info" className="text-[10px]">👥 নির্দিষ্ট</Badge>}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => handleDelete(a.id)}
                      className="p-2 text-ink-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors flex-shrink-0">
                      <HiOutlineTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnnouncements;